import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MasterProduct {
  id: string;
  barcode: string | null;
  product_name: string;
  retail_price: number | null;
  category_id: string | null;
  needs_square_sync: boolean | null;
  data_source: string;
  created_at: string;
  updated_at: string;
  master_categories?: {
    category_name: string;
  };
}

export const useMasterProductSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: searchResults = [], isLoading, error } = useQuery({
    queryKey: ['master-product-search', debouncedSearchTerm],
    queryFn: async () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.length < 2) {
        return [];
      }

      const { data, error } = await supabase
        .from('master_products')
        .select(`
          *,
          master_categories(category_name)
        `)
        .eq('is_active', true)
        .or(`barcode.ilike.%${debouncedSearchTerm}%,product_name.ilike.%${debouncedSearchTerm}%`)
        .order('product_name', { ascending: true })
        .limit(10);

      if (error) throw error;
      return data as MasterProduct[];
    },
    enabled: debouncedSearchTerm.length >= 2
  });

  const searchByBarcode = useCallback(async (barcode: string): Promise<MasterProduct | null> => {
    if (!barcode) return null;

    const { data, error } = await supabase
      .from('master_products')
      .select(`
        *,
        master_categories(category_name)
      `)
      .eq('barcode', barcode)
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    return data as MasterProduct | null;
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    isLoading,
    error,
    searchByBarcode
  };
};